<template>
    <v-dialog
        v-model="dialog"
        width="620" persistent
    >
        <v-card :loading="loadSurveyViewConfigPresetsLoading">
            <v-btn 
                icon color="black" style="position: absolute; top: 8px; right: 8px;"
                @click="dialog = false"
            >
                <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-card-title>{{ $t('delivery.surveys.viewConfig.title') }}</v-card-title>
            <v-card-subtitle>{{ $t('delivery.surveys.viewConfig.subtitle') }}</v-card-subtitle>
            <v-card-text>
                <v-tabs v-model="tab" background-color="grey lighten-3" grow>
                    <v-tab tab-value="home">
                        <v-icon left>mdi-home</v-icon>
                        {{ $t('delivery.surveys.viewConfig.tabs.home') }}
                    </v-tab>
                    <v-tab tab-value="email">
                        <v-icon left>mdi-email</v-icon>
                        {{ $t('delivery.surveys.viewConfig.tabs.email') }}
                    </v-tab>
                </v-tabs>
            </v-card-text>
            <v-card-text>
                <v-form ref="form">
                    <v-row dense align="center">
                        <v-col cols="8">
                            <span class="font-weight-medium">
                                {{ $t('delivery.surveys.viewConfig.labels.enableSurvey') }}
                            </span>
                        </v-col>
                        <v-col cols="4" class="d-flex justify-end">
                            <v-switch v-model="enableSurvey" color="black" class="mt-0" hide-details></v-switch>
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col cols="12">
                            <v-autocomplete
                                :loading="fetchSurveysLoading"
                                outlined dense
                                :label="$t('delivery.surveys.viewConfig.labels.selectSurvey')"
                                :placeholder="$t('delivery.surveys.viewConfig.placeholders.selectSurvey')"
                                :items="surveyItems"
                                v-model="surveyConfig"
                                :rules="enableSurvey ? requiredRules : undefined"
                            ></v-autocomplete>
                        </v-col>
                    </v-row>
                    <template v-if="tab === 'home'">
                        <v-divider class="mb-4"></v-divider>
                        <v-row dense>
                            <v-col cols="12">
                                <span class="font-weight-medium">
                                    {{ $t('delivery.surveys.viewConfig.labels.homeBannerTexts') }}
                                </span>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="bannerTitle"
                                    outlined dense
                                    :label="$t('delivery.surveys.viewConfig.labels.bannerTitle')"
                                    :placeholder="$t('delivery.surveys.viewConfig.placeholders.bannerTitle')"
                                    :rules="enableSurvey ? requiredRules : undefined"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-textarea
                                    v-model="bannerBody"
                                    outlined dense rows="3"
                                    :label="$t('delivery.surveys.viewConfig.labels.bannerBody')"
                                    :placeholder="$t('delivery.surveys.viewConfig.placeholders.bannerBody')"
                                    :rules="enableSurvey ? requiredRules : undefined"
                                ></v-textarea>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="bannerButtonText"
                                    outlined dense
                                    :label="$t('delivery.surveys.viewConfig.labels.bannerButtonText')"
                                    :placeholder="$t('delivery.surveys.viewConfig.placeholders.bannerButtonText')"
                                    :rules="enableSurvey ? requiredRules : undefined"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </template>
                    <v-row dense>
                        <v-col cols="12">
                        <div
                            v-if="changesAreSaved" 
                            class="v-btn v-btn--block v-btn--is-elevated v-btn--has-bg theme--dark v-size--default success"
                        >
                            {{ $t('delivery.surveys.viewConfig.buttons.saved') }}
                        </div>
                        <v-btn 
                            v-else
                            block dark @click="saveChanges()"
                            color="black"
                            :loading="saveChangesLoading"
                        >
                            {{ $t('delivery.surveys.viewConfig.buttons.save') }}
                        </v-btn>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
    import SurveysProvider from '../../../../providers/SurveysProvider'
    import SurveyViewConfigProvider from '../../../../providers/SurveyViewConfigProvider'

    export default {
        name: "SurveyViewConfigsModal",
        props: {
            value: Boolean
        },
        data: () => ({
            tab: 'home',
            form: {
                surveyViewConfig: {
                    home: {
                        enable: false,
                        survey: null,
                        bannerTitle: '',
                        bannerBody: '',
                        bannerButtonText: ''
                    },
                    email: {
                        enable: false,
                        survey: null
                    }
                }
            },
            surveyItems: [],
            fetchSurveysLoading: false,
            saveChangesLoading: false,
            savedChanges: {},
            changesAreSaved: false,
            loadSurveyViewConfigPresetsLoading: false
        }),
        computed: {
            requiredRules(){
                return [v => !!v || this.$t('delivery.surveys.viewConfig.rules.required')]
            },
            dialog: {
                get(){
                    if(this.value) this.fetchSurveys()
                    return this.value
                },
                set(value){
                    this.$emit('input', value)
                }
            },
            enableSurvey: {
                get(){
                    return this.currentConfig.enable
                },
                set(value){
                    this.currentConfig.enable = value
                    this.updateChangesState()
                }
            },
            surveyConfig: {
                get(){
                    return this.currentConfig.survey
                },
                set(value){
                    this.currentConfig.survey = value
                    this.updateChangesState()
                }
            },
            bannerTitle: {
                get(){
                    return this.form.surveyViewConfig.home.bannerTitle
                },
                set(value){
                    this.form.surveyViewConfig.home.bannerTitle = value
                    this.updateChangesState()
                }
            },
            bannerBody: {
                get(){
                    return this.form.surveyViewConfig.home.bannerBody
                },
                set(value){
                    this.form.surveyViewConfig.home.bannerBody = value
                    this.updateChangesState()
                }
            },
            bannerButtonText: {
                get(){
                    return this.form.surveyViewConfig.home.bannerButtonText
                },
                set(value){
                    this.form.surveyViewConfig.home.bannerButtonText = value
                    this.updateChangesState()
                }
            },
            currentConfig(){
                return this.form.surveyViewConfig[this.tab]
            }
        },
        async mounted(){
            await this.fetchSurveys()
            await this.loadSurveyViewConfigPresets()
        },
        methods: {
            async loadSurveyViewConfigPresets(){
                try {
                    this.loadSurveyViewConfigPresetsLoading = true
                    const { data: { fetchSurveyViewConfig } } = await SurveyViewConfigProvider.fetchSurveyViewConfig()
                    const homeConfig = fetchSurveyViewConfig.find(item => item.view === 'home') || {}
                    const emailConfig = fetchSurveyViewConfig.find(item => item.view === 'email') || {}
                    this.form.surveyViewConfig.home = {
                        enable: homeConfig.enable,
                        survey: homeConfig.survey ? homeConfig.survey.id : null,
                        bannerTitle: homeConfig.bannerTitle || this.$t('delivery.surveys.viewConfig.placeholders.bannerTitle'),
                        bannerBody: homeConfig.bannerBody || this.$t('delivery.surveys.viewConfig.placeholders.bannerBody'),
                        bannerButtonText: homeConfig.bannerButtonText || this.$t('delivery.surveys.viewConfig.placeholders.bannerButtonText')
                    }
                    this.form.surveyViewConfig.email = {
                        enable: emailConfig.enable,
                        survey: emailConfig.survey ? emailConfig.survey.id : null
                    }
                    this.savedChanges = JSON.parse(JSON.stringify(this.form))
                    this.updateChangesState()
                } catch (error) {
                    this.errorAlert = {
                        enable: true,
                        message: this.$t('delivery.surveys.viewConfig.errors.preload')
                    }
                } finally {
                    this.loadSurveyViewConfigPresetsLoading = false
                }
            },
            async fetchSurveys(){
                try {
                    this.fetchSurveysLoading = true
                    const {data: {fetchSurvey}} = await SurveysProvider.fetchSurvey()
                    this.surveyItems = fetchSurvey.map(survey => ({
                        value: survey.id,
                        text: survey.name
                    }))
                } catch (error) {
                    this.errorAlert = {
                        enable: true,
                        message: this.$t('delivery.surveys.viewConfig.errors.surveys')
                    }
                } finally {
                    this.fetchSurveysLoading = false
                }
            },
            async saveChanges(){
                try {
                    if(!this.$refs.form.validate()) return
                    this.saveChangesLoading = true
                    const surveyViewConfig = [
                        {view: 'home', ...this.form.surveyViewConfig.home},
                        {view: 'email', ...this.form.surveyViewConfig.email}
                    ]
                    await SurveyViewConfigProvider.updateSurveyViewConfig(surveyViewConfig)
                    this.savedChanges = JSON.parse(JSON.stringify(this.form))
                    this.changesAreSaved = JSON.stringify(this.savedChanges) === JSON.stringify(this.form)
                } catch (error) {
                    this.errorAlert = {
                        enable: true,
                        message: this.$t('delivery.surveys.viewConfig.errors.save')
                    }
                } finally {
                    this.saveChangesLoading = false
                }
            },
            updateChangesState(){
                this.changesAreSaved = JSON.stringify(this.savedChanges) === JSON.stringify(this.form)
            }
        }
    }
</script>
